import type { Editor } from "@tiptap/core";
import type { SuggestionOptions } from "@tiptap/suggestion";
import { PluginKey } from "@tiptap/pm/state";
import { extendMentionInsertRange } from "@/components/documents/extensions/extendMentionInsertRange";

/**
 * Mirrors @tiptap/extension-mention `getSuggestionOptions` so we can build options
 * and pass them to our forked `Suggestion` plugin.
 *
 * @param editor - Tiptap editor
 * @param extensionName - Node name (e.g. `dynamicMention`)
 * @param char - Trigger character
 * @param overrideSuggestionOptions - Options merged last (may include `pluginKey`)
 */
export function getDynamicMentionSuggestionOptions({
  editor: tiptapEditor,
  extensionName,
  char = "@",
  overrideSuggestionOptions,
}: {
  editor: Editor;
  extensionName: string;
  char?: string;
  overrideSuggestionOptions: Omit<SuggestionOptions, "editor">;
}): SuggestionOptions {
  const pluginKey = new PluginKey();

  return {
    editor: tiptapEditor,
    char,
    pluginKey,
    command: ({
      editor,
      range,
      props,
    }: {
      editor: any;
      range: any;
      props: any;
    }) => {
      const insertRange = extendMentionInsertRange(editor.state.doc, range);

      editor
        .chain()
        .focus()
        .insertContentAt(insertRange, [
          {
            type: extensionName,
            attrs: { ...props, mentionSuggestionChar: char },
          },
          {
            type: "text",
            text: " ",
          },
        ])
        .run();

      editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd();
    },
    allow: ({ state, range }: { state: any; range: any }) => {
      const $from = state.doc.resolve(range.from);
      const type = state.schema.nodes[extensionName];
      return !!$from.parent.type.contentMatch.matchType(type);
    },
    ...overrideSuggestionOptions,
  };
}
